import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Alert,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import { Edit as EditIcon, Lock as LockIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface User {
  id: number;
  email: string;
  greenCoins: number;
  goldCoins: number;
  packageType: 'STANDARD' | 'GOLD';
  created_at: string;
  updated_at: string;
}

const Admin: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [passwordDialog, setPasswordDialog] = useState<{ open: boolean; userId: number | null }>({ open: false, userId: null });
  const [coinsDialog, setCoinsDialog] = useState<{ 
    open: boolean; 
    userId: number | null; 
    currentGreenCoins: number;
    currentGoldCoins: number;
  }>({ open: false, userId: null, currentGreenCoins: 0, currentGoldCoins: 0 });
  const [newPassword, setNewPassword] = useState('');
  const [newGreenCoins, setNewGreenCoins] = useState('');
  const [newGoldCoins, setNewGoldCoins] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (user?.email !== 'ngoclinh15994@gmail.com') {
      navigate('/');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.ADMIN.USERS);
      setUsers(response.data.users);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching users');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!passwordDialog.userId || !newPassword) return;

    try {
      await axios.post(API_ENDPOINTS.ADMIN.UPDATE_PASSWORD, {
        userId: passwordDialog.userId,
        newPassword
      });
      setSuccessMessage('Password updated successfully');
      setPasswordDialog({ open: false, userId: null });
      setNewPassword('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error updating password');
    }
  };

  const handleUpdateCoins = async () => {
    if (!coinsDialog.userId) return;

    try {
      const updateData: any = { userId: coinsDialog.userId };
      if (newGreenCoins) updateData.greenCoins = parseInt(newGreenCoins);
      if (newGoldCoins) updateData.goldCoins = parseInt(newGoldCoins);

      await axios.post(API_ENDPOINTS.ADMIN.UPDATE_COINS, updateData);
      setSuccessMessage('Coins updated successfully');
      setCoinsDialog({ open: false, userId: null, currentGreenCoins: 0, currentGoldCoins: 0 });
      setNewGreenCoins('');
      setNewGoldCoins('');
      fetchUsers(); // Refresh user list to get updated coins
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error updating coins');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          User Management
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        <TableContainer>
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Package</TableCell>
                <TableCell>Green Coins</TableCell>
                <TableCell>Gold Coins</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Updated At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.id}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Box sx={{ 
                      display: 'inline-block',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: user.packageType === 'GOLD' ? 'warning.main' : 'grey.300',
                      color: user.packageType === 'GOLD' ? 'warning.contrastText' : 'text.primary',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {user.packageType}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span style={{ color: 'rgb(0, 255, 13)' }}>🟢</span>
                      {user.greenCoins}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>🟡</span>
                      {user.goldCoins}
                    </Box>
                  </TableCell>
                  <TableCell>{new Date(user.created_at).toLocaleString()}</TableCell>
                  <TableCell>{new Date(user.updated_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => setPasswordDialog({ open: true, userId: user.id })}
                        title="Change Password"
                      >
                        <LockIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setCoinsDialog({ 
                          open: true, 
                          userId: user.id, 
                          currentGreenCoins: user.greenCoins,
                          currentGoldCoins: user.goldCoins
                        })}
                        title="Update Coins"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Password Update Dialog */}
      <Dialog open={passwordDialog.open} onClose={() => setPasswordDialog({ open: false, userId: null })}>
        <DialogTitle>Update Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog({ open: false, userId: null })}>Cancel</Button>
          <Button onClick={handleUpdatePassword} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Coins Update Dialog */}
      <Dialog open={coinsDialog.open} onClose={() => setCoinsDialog({ open: false, userId: null, currentGreenCoins: 0, currentGoldCoins: 0 })}>
        <DialogTitle>Update Coins</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Green Coins"
              type="number"
              fullWidth
              value={newGreenCoins}
              onChange={(e) => setNewGreenCoins(e.target.value)}
              helperText={`Current green coins: ${coinsDialog.currentGreenCoins}`}
              InputProps={{
                startAdornment: <span style={{ color: 'rgb(0, 255, 13)', marginRight: 8 }}>🟢</span>
              }}
            />
            <TextField
              margin="dense"
              label="Gold Coins"
              type="number"
              fullWidth
              value={newGoldCoins}
              onChange={(e) => setNewGoldCoins(e.target.value)}
              helperText={`Current gold coins: ${coinsDialog.currentGoldCoins}`}
              InputProps={{
                startAdornment: <span style={{ marginRight: 8 }}>🟡</span>
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCoinsDialog({ open: false, userId: null, currentGreenCoins: 0, currentGoldCoins: 0 })}>Cancel</Button>
          <Button onClick={handleUpdateCoins} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Admin; 